import { ApiError } from './problem';

/**
 * How a failure should be *described*, separated from where it is shown.
 *
 * Both an inline `Notice` and a corner toast have to answer the same two questions — what heading
 * goes above the server's wording, and what colour it deserves — and they must answer them the same
 * way. Holding the rules here is what stops a plan limit rendering as a quiet boundary in one place
 * and an alarming red error in the other.
 *
 * The headings are literals for now; they become resource keys with i18n in 1.7.9 (rule 13).
 */

/** The tones {@link Notice} understands, so a caller never has to translate between vocabularies. */
export type ProblemTone = 'info' | 'warning' | 'danger';

/**
 * The heading above the server's own wording.
 *
 * The body is always the server's `detail`, never text invented here: the API resolves it from its
 * own resource files, and when a request fails several ways at once it deliberately reports the
 * most fixable one first — subscription before plan before permission.
 *
 * What the client adds is the heading, because that is where the *audience* differs: an unpaid
 * invoice is for the owner, a missing feature is a sales conversation, a cap is something their own
 * administrator can free, and a denied permission is that administrator for another reason.
 */
export function problemHeading(error: ApiError): string {
  switch (error.refusalKind) {
    case 'subscription':
      return 'Your subscription needs attention';
    case 'plan':
      return 'Not included in your plan';
    case 'limit':
      return 'You have reached a limit';
    case 'permission':
      return 'You do not have access to this';
    default:
      break;
  }

  if (error.isUnauthenticated) {
    return 'Please sign in again';
  }

  return error.problem?.title ?? 'Request failed';
}

/**
 * A plan limit or a missing feature is not an error the user made — it is a boundary, and one with
 * a commercial answer. Colouring it red teaches people to read the app as broken.
 */
export function problemTone(error: ApiError): ProblemTone {
  switch (error.refusalKind) {
    case 'plan':
      return 'info';
    case 'limit':
    case 'subscription':
      return 'warning';
    default:
      return 'danger';
  }
}
